// Timezone-safe date helpers.
//
// Why this exists:
//   `new Date().toISOString().slice(0, 10)` returns the UTC date, not the
//   user's local date. If a tech in EST opens the app at 8 PM on Sept 14,
//   JS thinks "today" is already Sept 15 (because UTC is 5 hours ahead).
//   Same bug in reverse when they PICK a date in an <input type="date">:
//   `new Date("2026-09-15").toISOString()` = "2026-09-15T00:00:00.000Z"
//   which displays as Sept 14 in EST — the classic off-by-one.
//
// Everything in Field Radar that touches a *calendar day* (schedule dates,
// due dates, "today" filters) must use these helpers instead of raw Date
// arithmetic. Only wall-clock timestamps (created_at, clock_in_at) should
// keep using ISO/UTC.

/**
 * Return today's date in the browser's local timezone as YYYY-MM-DD.
 * Use this instead of `new Date().toISOString().slice(0, 10)`.
 */
export function todayLocalISO() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * Convert an <input type="date"> value ("YYYY-MM-DD") to an ISO string that
 * anchors to local midnight instead of UTC midnight. Use this before POSTing
 * a picked date to the backend if the backend stores full ISO strings.
 *
 * Example: EST user picks "2026-09-15"
 *   BAD:  new Date("2026-09-15").toISOString() = "2026-09-15T00:00:00Z" → Sept 14 EST
 *   GOOD: dateInputToISO("2026-09-15") = "2026-09-15T00:00:00-05:00"    → Sept 15 EST
 */
export function dateInputToISO(value) {
  if (!value) return "";
  const [y, m, d] = value.split("-").map(Number);
  if (!y || !m || !d) return "";
  // Anchor at noon LOCAL to avoid DST-transition edge cases where midnight
  // straddles a "spring forward" / "fall back" and rolls the wall-clock day.
  return new Date(y, m - 1, d, 12, 0, 0, 0).toISOString();
}

/**
 * Convert an ISO string back to a "YYYY-MM-DD" suitable for
 * <input type="date">. Always uses the LOCAL date part, matching what the
 * user actually sees on their calendar.
 */
export function isoToDateInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * Display an ISO string (or a YYYY-MM-DD) as "M/D/YYYY" using LOCAL time —
 * never UTC. Safer than `new Date(iso).toLocaleDateString()` when the ISO
 * happens to be UTC-anchored midnight from an older buggy write.
 */
export function displayLocalDate(iso) {
  if (!iso) return "";
  // Bare YYYY-MM-DD strings: parse as local (never UTC) so Sept 15 stays Sept 15.
  if (typeof iso === "string" && /^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    const [y, m, d] = iso.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString();
  }
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString();
}
